import React from 'react'
import PropTypes from 'prop-types'
import { useQuery } from '@apollo/client'
import { useSelector } from 'react-redux'
import _ from 'lodash'

import SelectInvoiceServiceLineItems from '../../components/Invoices/SelectInvoiceServiceLineItems'
import { GET_SERVICE_LINE_ITEMS } from '../../graphql/serviceLineItems'

const SelectInvoiceServiceLineItemsContainer = ({
    onChange,
    orderId,
    value
}) => {
    const { data, loading, refetch } = useQuery(GET_SERVICE_LINE_ITEMS, {
        variables: { orderNumber: orderId },
        skip: !orderId
    })
    const vatIncluded = useSelector((state) => state.getIn(['Entities', 'orders', orderId, 'vat_included'], false))

    const getServiceLineItems = () => {
        const edges = _.get(data, 'order.serviceLineItems.edges', [])

        return edges.map(({ node }) => {
            const itemId = _.toString(_.get(node, 'id', ''))

            return {
                id: `ServiceLineItem:${itemId}`,
                item_id: itemId,
                item_type: 'ServiceLineItem',
                name: _.get(node, 'name', ''),
                quantity: _.get(node, 'quantity', 0),
                unit: _.get(node, 'unit', ''),
                single_price: _.get(node, 'singlePrice', '0'),
                empty_line_below: ''
            }
        })
    }

    const getDefaultServiceLineItems = () => {
        const standaloneIds = new Set(getServiceLineItems().map(item => item.item_id))

        return (value || []).filter((invoiceItem) => {
            const itemId = _.toString(_.get(invoiceItem, 'item_id', ''))
            return standaloneIds.has(itemId)
        }).map((invoiceItem) => {
            const itemId = _.toString(_.get(invoiceItem, 'item_id', ''))

            return {
                id: `ServiceLineItem:${itemId}`,
                item_id: itemId,
                item_type: 'ServiceLineItem',
                name: _.get(invoiceItem, 'name', ''),
                quantity: _.get(invoiceItem, 'quantity', 0),
                unit: _.get(invoiceItem, 'unit', ''),
                single_price: _.get(invoiceItem, 'money_display.single_price', '0'),
                empty_line_below: _.get(invoiceItem, 'empty_line_below', '')
            }
        })
    }

    const handleFetchInitialData = ({ onSuccess } = {}) => {
        refetch().then(() => {
            if (onSuccess) onSuccess()
        })
    }

    const onSelectServiceLineItemsChange = (selectedItems) => {
        const selectedInvoiceItems = selectedItems.map((item) => {
            const compositeId = _.get(item, 'id', '')
            const [itemType, itemId] = compositeId.split(':')

            const invoiceItem = _.find(value || [], (v) => {
                return _.toString(_.get(v, 'item_id', '')) === itemId &&
                    _.get(v, 'item_type', '') === itemType
            })
            const price = _.get(item, 'single_price', '0')

            return {
                id: _.get(invoiceItem, 'id', ''),
                item_id: itemId,
                item_type: 'ServiceLineItem',
                name: _.get(item, 'name', ''),
                quantity: _.get(item, 'quantity', 0),
                unit: _.get(item, 'unit', ''),
                empty_line_below: _.get(item, 'empty_line_below', ''),
                single_price: price ? price : '0',
                _destroy: _.get(item, '_destroy', false)
            }
        })

        onChange(selectedInvoiceItems)
    }

    return (
        <SelectInvoiceServiceLineItems
            defaultServiceLineItems={getDefaultServiceLineItems()}
            initialData={getServiceLineItems()}
            loading={loading}
            onFetchInitialData={handleFetchInitialData}
            onSelectServiceLineItemsChange={onSelectServiceLineItemsChange}
            orderId={orderId}
            vatIncluded={vatIncluded}
        />
    )
}

SelectInvoiceServiceLineItemsContainer.propTypes = {
    onChange: PropTypes.func,
    orderId: PropTypes.string.isRequired,
    value: PropTypes.array
}

SelectInvoiceServiceLineItemsContainer.defaultProps = {
    onChange: () => {},
    value: []
}

export default SelectInvoiceServiceLineItemsContainer
